import { useCallback, useLayoutEffect, useRef } from 'react'
import { toast } from 'react-common'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
import { AggregateApi, ObjectiveWorkflowAggregate } from '@/api/models/aggregate'
import { CreateSelfEnrollmentsApi } from '@/api/models/createSelfEnrollments'
import { useCourseNavigate } from '@/hooks/useCourseNavigate'
import { useAppDispatch } from '@/store/hooks'
import { addBlockingOperation, removeBlockingOperation } from '@/store/slices/loadingSlice'
import { log } from '@/utils/log'
import { useIsFirstLoginAfterRegister } from '@/views/PublicCatalogRouter/hooks/useIsFirstLoginAfterRegister'
import { useWaitForObjectiveWorkflowAggregate } from '@/views/PublicCatalogRouter/hooks/useWaitForObjectiveWorkflowAggregate'
import { getOpenCatalogFlowParam, OPEN_CATALOG_COURSE_FLOW } from './openCatalogCourseFlow'

const createSelfEnrollment = async (objectiveAccessId: string) => {
  const entityId = uuid()
  await CreateSelfEnrollmentsApi.create({
    id: entityId,
    objectiveAccessId,
  })
}

const startObjectiveWorkflow = async (aggregateId: string) => {
  const entityId = uuid()
  await AggregateApi.startObjectiveWorkflow(aggregateId, entityId)
}

export const useOpenCatalogFlow = () => {
  const isFirstLoginAfterRegister = useIsFirstLoginAfterRegister()

  const flow = getOpenCatalogFlowParam()
  const dispatch = useAppDispatch()
  const isLoadingRef = useRef(false)

  const { t } = useTranslation()
  const navigate = useNavigate()
  const { navigateToContent } = useCourseNavigate()

  const removeLoader = useCallback(() => {
    isLoadingRef.current = false
    dispatch(removeBlockingOperation())
  }, [dispatch])

  const extractErrorAndShowToast = useCallback((error: any) => {
    const firstError = error?.response?.data?._?.[0]?.message

    if (firstError) {
      toast.critical(firstError)
    }
  }, [])

  const showCourseEnrolledToast = useCallback(
    (courseName: string) => {
      toast.success(
        t('publicCatalog.courseEnrolled', {
          courseName,
        }),
      )
    },
    [t],
  )

  const navigateToCourses = useCallback(() => {
    navigate('/courses', {
      replace: true,
    })
    log('[useOpenCatalogFlow] navigateToCourses')
  }, [navigate])

  const { waitForObjectiveWorkflowAggregate } = useWaitForObjectiveWorkflowAggregate()

  const handleFlow = useCallback(
    async (startTheCourse?: boolean) => {
      if (isLoadingRef.current) {
        return
      }

      dispatch(addBlockingOperation())
      isLoadingRef.current = true

      let aggregate: ObjectiveWorkflowAggregate | undefined

      try {
        aggregate = await waitForObjectiveWorkflowAggregate()
      } catch (e) {
        removeLoader()
        console.error(e)
        toast.critical(t('_errors.unknown'))
        navigateToCourses()
        return
      }

      try {
        await createSelfEnrollment(aggregate!.objectiveAccess!.id)
      } catch (error: any) {
        extractErrorAndShowToast(error)
        removeLoader()
        return
      }

      // go to my courses again
      if (!startTheCourse) {
        removeLoader()
        navigateToCourses()
        showCourseEnrolledToast(aggregate.objective.name)
        return
      }

      // go to course page
      try {
        await startObjectiveWorkflow(aggregate.id)
        showCourseEnrolledToast(aggregate.objective.name)
      } catch (error) {
        extractErrorAndShowToast(error)
        removeLoader()
        return
      }

      navigateToContent(aggregate.objective.id)

      removeLoader()
    },
    [
      dispatch,
      navigateToContent,
      removeLoader,
      navigateToCourses,
      extractErrorAndShowToast,
      showCourseEnrolledToast,
      waitForObjectiveWorkflowAggregate,
      t,
    ],
  )

  useLayoutEffect(() => {
    if (isFirstLoginAfterRegister) {
      return
    }

    switch (flow) {
      case OPEN_CATALOG_COURSE_FLOW.ADD_TO_MY_COURSES:
        handleFlow()
        break

      case OPEN_CATALOG_COURSE_FLOW.START_THE_COURSE:
        handleFlow(true)
        break

      default:
        break
    }
  }, [flow, handleFlow, isFirstLoginAfterRegister])
}
